"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import { DownloadIcon } from "lucide-react";
import { useState } from "react";
import { useClient } from "urql";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import type { ToolOutputRendererProps } from "../ui/types";
import { downloadIndexWorkerExportData } from "./client";
import type { IndexWorkerExportToolOutput } from "./output";

type Props = {
  output: IndexWorkerExportToolOutput;
};

function GetIndexWorkerExportDataOutput({ output }: Props) {
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();
  const { i18n } = useLingui();
  const client = useClient();

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadIndexWorkerExportData(client, output);
      toast({
        title: i18n.t("Success"),
        description: i18n.t("Export completed."),
      });
    } catch (error) {
      toast({
        title: i18n.t("Error"),
        description:
          error instanceof Error ? error.message : i18n.t("Export failed."),
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="p-3">
      <Button
        className="gap-2"
        disabled={isExporting}
        onClick={handleExport}
        size="sm"
        variant="default"
      >
        <DownloadIcon className="h-4 w-4" />
        {isExporting ? (
          <Trans>Exporting...</Trans>
        ) : output.exportRequest.exportTarget === "trends" ? (
          <Trans>Export chart data as CSV</Trans>
        ) : output.exportRequest.format === "excel" ? (
          <Trans>Export URL table as Excel</Trans>
        ) : output.exportRequest.format === "json" ? (
          <Trans>Export URL table as JSON</Trans>
        ) : (
          <Trans>Export URL table as CSV</Trans>
        )}
      </Button>
    </div>
  );
}

/** Registry-bound tool output renderer for get_index_worker_export_data. */
export function GetIndexWorkerExportDataToolOutput({
  output,
}: ToolOutputRendererProps<IndexWorkerExportToolOutput>) {
  return <GetIndexWorkerExportDataOutput output={output} />;
}
